import Clipboard from '@react-native-clipboard/clipboard';
import React, { FC, ReactChild, memo } from 'react';
import styled from 'styled-components/native';

import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { kSeparatorForNumberOrDecimals } from '@webtrader/core/format/price';
import { useTranslation } from '@webtrader/core/i18n';
import { useDemoCreation } from '@webtrader/core/services/demoCreation';
import { useTheme } from '@webtrader/core/services/theme/v2';
import { checkIsNative } from '@webtrader/core/validators/device';

import { AlertManager } from '@/components/Alert/Manager';
import AntDesign from '@/components/Icons/AntDesign';
import IonicIcon from '@/components/Icons/Ionic';
import MaterialIcon from '@/components/Icons/Material';
import Text from '@/components/Text';
import TouchableOpacity from '@/components/Touch/Opacity';

interface IProps {}

const DemoCompletionContent: FC<IProps> = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { demoAccountInfo } = useDemoCreation();
  const { login, password, investorPassword, name, leverage, deposit, currency } = demoAccountInfo || {};

  const onCopy = () => {
    let textToCopy = '';

    if (login) {
      textToCopy += `Login: ${login}\n`;
    }
    if (password) {
      textToCopy += `Password: ${password}\n`;
    }
    if (investorPassword) {
      textToCopy += `View Only Password: ${investorPassword}\n`;
    }
    if (name) {
      textToCopy += `Name: ${name}\n`;
    }
    if (leverage) {
      textToCopy += `Leverage: 1 : ${leverage}\n`;
    }
    if (deposit || currency) {
      textToCopy += `Deposit: ${kSeparatorForNumberOrDecimals(deposit ?? 0)} ${currency}\n`;
    }

    if (checkIsNative()) {
      Clipboard.setString(textToCopy);
    } else {
      navigator.clipboard.writeText(textToCopy);
    }
    AlertManager().alert({
      message: t('Copied to Clipboard')
    });
  };

  const demoCompletionContent = (icon: ReactChild, title: string, value?: string | number) => {
    if (!value) {
      return null;
    }

    return (
      <ContentRow>
        {icon}
        <ContentInfo>
          <ContentInfoTitle testID={EDataTestId.DEMO_COMPLETION_LABEL}>{title}</ContentInfoTitle>
          <ContentInfoText testID={EDataTestId.DEMO_COMPLETION_VALUE}>{value}</ContentInfoText>
        </ContentInfo>
      </ContentRow>
    );
  };

  if (!demoAccountInfo || demoAccountInfo === undefined) {
    return null;
  }

  return (
    <>
      <CopyContainer>
        <CopyTitle>{t('Personal Information')}</CopyTitle>
        <CopyTouch onPress={() => onCopy()} testID={EDataTestId.DEMO_ACCOUNT_COMPLETION_MODAL_COPY}>
          <CopyTitle>{t('Copy')}</CopyTitle>
        </CopyTouch>
      </CopyContainer>
      <Content>
        {demoCompletionContent(<AntDesign name="user" size={16} color={theme.colors.text[1]} />, t('Login'), login)}
        {demoCompletionContent(
          <MaterialIcon name="lock-outline" size={16} color={theme.colors.text[1]} />,
          t('Password'),
          password
        )}
        {demoCompletionContent(
          <MaterialIcon name="lock-outline" size={16} color={theme.colors.text[1]} />,
          t('View Only Password'),
          investorPassword
        )}
        {demoCompletionContent(<AntDesign name="user" size={16} color={theme.colors.text[1]} />, t('Name'), name)}
        {leverage &&
          demoCompletionContent(
            <MaterialIcon name="groups" size={16} color={theme.colors.text[1]} />,
            t('Leverage'),
            `1 : ${leverage}`
          )}
        {demoCompletionContent(
          <IonicIcon name="logo-usd" size={16} color={theme.colors.text[1]} />,
          t('Deposit'),
          `${kSeparatorForNumberOrDecimals(deposit ?? 0)} ${currency}`
        )}
      </Content>
    </>
  );
};

export default memo(DemoCompletionContent);

const CopyContainer = styled.View`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: ${dynamicSize.convertSize(12)}px ${({ theme }) => theme.sizes.mobile.basePaddingStart}px;

  background-color: ${({ theme }) => theme.colors.background[1]};
`;

const CopyTouch = styled(TouchableOpacity)``;

const CopyTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.text[1]};
  font-size: ${dynamicSize.convertSize(15)}px;
`;

const Content = styled.View`
  background-color: ${({ theme }) => theme.colors.background[0]};
  padding: ${dynamicSize.convertSize(8)}px ${dynamicSize.convertSize(16)}px;
`;

const ContentRow = styled.View`
  display: flex;
  flex-direction: row;
  align-items: center;

  padding: ${dynamicSize.convertSize(8)}px 0;
`;

const ContentInfo = styled.View`
  flex: 1;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  margin-start: ${dynamicSize.convertSize(16)}px;
`;

const ContentInfoTitle = styled(Text)`
  max-width: 48%;
  color: ${({ theme }) => theme.colors.text[0]};
  font-size: ${dynamicSize.convertSize(16)}px;
`;

const ContentInfoText = styled(ContentInfoTitle)`
  text-align: ${() => (checkIsNative() ? 'right' : 'end')};
`;
